import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@shared/contexts/auth-context";
import { useBalance } from "@shared/hooks/useBalance";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  Skeleton
} from "@shared/ui/components";
import { Clock, CreditCard, FileText, History, LogOut, Menu, Send, ShieldCheck, User, Zap } from "lucide-react";

export const Header: React.FC = () => {
  const { user, isAuthenticated, isLoading: isAuthLoading, signOut, openAuthModal } = useAuth();
  const { balance, loading, refetch } = useBalance();

  const navItems = [
    { name: "Возможности", url: "/#features", icon: Zap },
    { name: "Тарифы", url: "/pricing", icon: CreditCard },
    { name: "Telegram", url: "/telegram", icon: Send },
    { name: "История", url: "/history", icon: History }
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container">
        {/* Высота: 56px на мобильном (вторая строка навигации уехала в шит
            по бургеру — она съедала место на каждом экране) и 64px от md */}
        <div className="flex h-14 items-center md:h-16">
          <Link to="/" className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {/* <PolotnoLogo className="h-6 w-20 md:h-8 md:w-24 text-primary" /> */}
            <span className="font-rublev text-lg md:text-xl font-normal tracking-[0.02em] text-foreground translate-y-[3px]">
              ПОЛОТНО
            </span>
          </Link>

          <nav className="hidden md:flex absolute left-1/2 -translate-x-1/2 space-around gap-x-6 text-sm font-medium text-muted-foreground">
            {navItems.map(item => (
              <NavLink
                key={item.name}
                to={item.url}
                className={({ isActive }) =>
                  `border-b-2 pb-1 text-sm transition-colors ${
                    isActive
                      ? "border-foreground font-semibold text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 ml-auto md:gap-3">
            {/* Баланс минут на мобильном вынесен наружу: внутри выпадающего меню
                его не видно, а упирается в него пользователь чаще всего */}
            {isAuthenticated && balance && (
              <Link
                to="/subscription"
                className="flex h-8 items-center gap-1.5 rounded-full border border-border px-2.5 text-xs text-muted-foreground md:hidden"
              >
                <Clock className="h-3.5 w-3.5" />
                <span className="font-semibold text-foreground">{balance.availableMinutes}</span>
              </Link>
            )}

            {isAuthenticated && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden" aria-label="Меню">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] p-0">
                  <SheetTitle className="sr-only">Меню</SheetTitle>
                  <div className="flex flex-col gap-1 p-4 pt-12">
                    <div className="mb-2 flex flex-col gap-0.5 px-3">
                      <span className="text-sm font-medium">{user?.name}</span>
                      <span className="text-xs text-muted-foreground">{user?.email}</span>
                    </div>

                    {navItems.map(item => (
                      <SheetClose asChild key={item.name}>
                        <NavLink
                          to={item.url}
                          className={({ isActive }) =>
                            `flex h-11 items-center gap-3 rounded-md px-3 text-sm transition-colors ${
                              isActive ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"
                            }`
                          }
                        >
                          <item.icon className="h-4 w-4" />
                          {item.name}
                        </NavLink>
                      </SheetClose>
                    ))}

                    <div className="my-2 h-px bg-border" />

                    <SheetClose asChild>
                      <Link
                        to="/subscription"
                        className="flex h-11 items-center gap-3 rounded-md px-3 text-sm text-muted-foreground"
                      >
                        <CreditCard className="h-4 w-4" />
                        Управление подпиской
                      </Link>
                    </SheetClose>
                    {user?.role === "superadmin" && (
                      <SheetClose asChild>
                        <Link
                          to="/admin"
                          className="flex h-11 items-center gap-3 rounded-md px-3 text-sm text-muted-foreground"
                        >
                          <ShieldCheck className="h-4 w-4" />
                          Админ Панель
                        </Link>
                      </SheetClose>
                    )}
                    <SheetClose asChild>
                      <button
                        type="button"
                        onClick={signOut}
                        className="flex h-11 items-center gap-3 rounded-md px-3 text-sm text-destructive"
                      >
                        <LogOut className="h-4 w-4" />
                        Выйти
                      </button>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            )}

            {isAuthenticated ? (
              <DropdownMenu
                onOpenChange={open => {
                  if (open) {
                    refetch();
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hidden gap-2 md:inline-flex">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  {loading && !balance ? (
                    <div className="px-2 py-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-8" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-12" />
                      </div>
                      <div className="flex items-center justify-between text-muted-foreground">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-8" />
                      </div>
                    </div>
                  ) : (
                    balance && (
                      <>
                        <div className="px-2 py-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">Тариф:</span>
                            </div>
                            <span className="text-sm font-semibold text-primary">{balance.planName}</span>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">Доступно:</span>
                            </div>
                            <span className="text-sm font-semibold">{balance.availableMinutes}</span>
                          </div>

                          {balance.reservedMinutes > 0 && (
                            <div className="flex items-center justify-between text-amber-600">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span className="text-xs font-medium">В обработке:</span>
                              </div>
                              <span className="text-xs font-semibold">{balance.reservedMinutes} мин</span>
                            </div>
                          )}

                          <div className="flex items-center justify-between text-muted-foreground">
                            <span className="text-xs">Всего минут:</span>
                            <span className="text-xs">{balance.totalMinutes}</span>
                          </div>

                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">ИИ-вопросы:</span>
                            </div>
                            <span className="text-sm font-semibold">
                              {balance.remainingSummaries}/{balance.includedSummaries}
                            </span>
                          </div>
                        </div>

                        <DropdownMenuSeparator />
                      </>
                    )
                  )}

                  <DropdownMenuItem asChild>
                    <Link to="/history" className="cursor-pointer">
                      <History className="mr-2 h-4 w-4" />
                      История транскрипций
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/subscription" className="cursor-pointer">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Управление подпиской
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === "superadmin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          Админ Панель
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Выйти
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : isAuthLoading ? (
              // Профиль ещё едет по cookie: показать «Войти» здесь — значит
              // мигнуть кнопкой входа залогиненному пользователю на каждый F5
              <>
                <Skeleton className="hidden h-9 w-[76px] rounded-md md:block" />
                <Skeleton className="h-9 w-[116px] rounded-md" />
              </>
            ) : (
              <>
                {/* Вход спрятан на мобильном: места мало, а из формы регистрации
                    в него уводит ссылка внутри модалки */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden md:inline-flex"
                  onClick={() => openAuthModal("signin")}
                >
                  Войти
                </Button>
                <Button size="sm" className="bg-primary hover:bg-primary-dark" onClick={() => openAuthModal("signup")}>
                  Регистрация
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
